import React, { FC, useEffect, useMemo } from "react";
import InputMask from "react-input-mask";

import BaseModal from "components/common/BaseModal/BaseModal";
import { IBaseModalProps } from "core/types/elements/IModal";
import { Controller, useForm } from "react-hook-form";
import { formatPhoneNumber, toggleInvalidInput, validationResolver } from "core/helpers";
import { TFormUser } from "core/types/base/IUser";
import { addAdminSchema, editAdminSchema } from "core/helpers/ValidationSchemas";
import { ICustomReactSelectOption } from "core/types/elements/ICustomReactSelectOption";
import CustomReactSelector from "components/common/Selector/CustomReactSelector";
import { useRoles } from "hooks/admin";
import { GlobalAdminRole } from "../tenants/helpers/isGlobalAdmin";

interface IProps extends IBaseModalProps<TFormUser> {
  user?: TFormUser;
  tenantId?: string;
}

const AddEditAdminModal: FC<IProps> = ({
  onClose,
  showModal,
  user,
  onSave,
  modalTitle,
  tenantId,
}) => {
  const isEditMode = useMemo(() => (user && Object.keys(user).length) !== 0, [user]);
  const isGlobalAdmin = !tenantId; // If no tenantId, this is for global admins
  const { roleOptions, loading: rolesLoading } = useRoles({ fetchOnLoad: true, tenantId });
  const {
    register,
    formState: { errors },
    handleSubmit,
    reset,
    control,
  } = useForm<TFormUser>({
    resolver: validationResolver(isEditMode ? editAdminSchema : addAdminSchema),
    // For global admins, we don't need role validation since it's always GlobalAdminRole
    mode: isGlobalAdmin ? "onChange" : "onChange",
  });

  useEffect(
    () => {
      if (isEditMode && user) {
        // For edit mode, preserve the existing role value properly
        // Extract role from roles array if role field is empty
        const userRole = isGlobalAdmin
          ? GlobalAdminRole
          : user.role || (user.roles && user.roles[0]) || "";
        reset({
          ...user,
          email: "", // Clear email in edit mode as per business logic
          role: userRole,
        });
      } else {
        // For create mode, set default values
        reset({
          firstName: "",
          lastName: "",
          phoneNumber: "",
          email: "",
          role: isGlobalAdmin ? GlobalAdminRole : "",
        } as TFormUser);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, showModal, isGlobalAdmin],
  );

  const onFormSubmit = (formData: TFormUser) => {
    const { email, firstName, lastName, phoneNumber, role } = formData;
    if (onSave) {
      // Determine roles based on admin type
      const userRole = isGlobalAdmin ? GlobalAdminRole : role || "";
      let userRoles: string[] = [];
      if (isGlobalAdmin) {
        userRoles = [GlobalAdminRole];
        // eslint-disable-next-line no-console
        console.log("Creating Global Admin with role:", GlobalAdminRole);
      } else if (role) {
        userRoles = [role as string];
        // eslint-disable-next-line no-console
        console.log("Creating Tenant Admin with role:", role);
      }
      onSave({
        email,
        firstName,
        lastName,
        phoneNumber: phoneNumber || "",
        role: userRole,
        roles: userRoles,
        id: "",
      });
    }
  };

  const modalBody = () => (
    <form className="row" autoComplete="off">
      <div
        className="form-group col-12 col-lg-6
       mb-3"
      >
        <label className="required" htmlFor="firstName">
          First Name
        </label>
        <input
          type="text"
          placeholder="Enter First Name ..."
          className={`shadow-sm form-control ${toggleInvalidInput(!!errors?.firstName)}`}
          autoComplete="off"
          {...register("firstName")}
        />
      </div>
      <div
        className="form-group col-12 col-lg-6
       mb-3"
      >
        <label className="required" htmlFor="lastName">
          Last Name
        </label>
        <input
          type="text"
          placeholder="Enter Last Name ..."
          className={`shadow-sm form-control ${toggleInvalidInput(!!errors?.lastName)}`}
          autoComplete="off"
          {...register("lastName")}
        />
      </div>
      {!isEditMode && (
        <div
          className="form-group col-12 col-lg-6
       mb-3"
        >
          <label className="required" htmlFor="email">
            Email
          </label>
          <input
            type="email"
            placeholder="Enter Email ..."
            className={`shadow-sm form-control ${toggleInvalidInput(!!errors?.email)}`}
            autoComplete="off"
            {...register("email")}
          />
        </div>
      )}
      <div className="form-group col-12 col-lg-6 mb-3 position-relative">
        <label className="required" htmlFor="phoneNumber">
          Phone Number
        </label>
        <Controller
          control={control}
          name="phoneNumber"
          rules={{ required: true }}
          render={({ field: { onChange, value }, fieldState }) => (
            <InputMask
              mask=""
              alwaysShowMask={false}
              value={value}
              onChange={(e) => onChange(formatPhoneNumber(e.target.value))}
              className={`form-control ${toggleInvalidInput(fieldState.invalid)}`}
              placeholder="+x (xxx)-xxx-xxxx"
            />
          )}
        />
        <p className="opacity-50 mt-1 fs-6 mb-0 phone-hint">
          Phone numbers should be formatted with a + and country code.
        </p>
      </div>
      {/* Only show role selection for tenant admins, not for global admins */}
      {!isGlobalAdmin && (
        <div className="form-group col-12 col-lg-6 mb-3">
          <label className="required" htmlFor="role">
            Role
          </label>
          <Controller
            control={control}
            name="role"
            render={({ field: { onChange, value }, fieldState }) => (
              <CustomReactSelector
                options={roleOptions}
                value={
                  roleOptions.find(
                    (option) =>
                      option.value === value ||
                      (typeof value === "object" && value?.value === option.value),
                  ) || null
                }
                onChange={(selectedOption: ICustomReactSelectOption | null) => {
                  // Pass the role name (which is the value) directly
                  onChange(selectedOption?.value || "");
                }}
                placeholder={rolesLoading ? "Loading roles..." : "Select a role..."}
                className={`${toggleInvalidInput(fieldState.invalid)}`}
                isDisabled={rolesLoading}
              />
            )}
          />
          {errors.role && <div className="invalid-feedback d-block">{errors.role.message}</div>}
        </div>
      )}
    </form>
  );
  return (
    <BaseModal
      modalTitle={modalTitle}
      onClose={onClose}
      onCancel={onClose}
      showModal={showModal}
      modalBody={modalBody()}
      onSave={handleSubmit(onFormSubmit)}
      cancelActionBtnText="Close"
      successBtnText="Save"
    />
  );
};

export default AddEditAdminModal;
